import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import CustomTypography from "../form/CustomTypography";
import { observer } from "mobx-react";

interface ImageGalleryWithDialogProps {
  images?: Array<{ [key: string]: any } | string> | null;
  title?: string;
  imageKey?: string;
  idKey?: string;
  initialVisibleCount?: number;
  thumbnailStyle?: React.CSSProperties;
  emptyMessage?: string;
  onImageClick?: (image: any, index: number) => void; // NEW: callback to open external popup
}

const ImageGalleryWithDialog: React.FC<ImageGalleryWithDialogProps> = ({
  images = [],
  title = "Images",
  imageKey = "image_url",
  idKey = "id",
  initialVisibleCount = 3,
  thumbnailStyle = {},
  emptyMessage = "No images found",
  onImageClick,
}) => {
  const theme = useTheme();

  const getImageUrl = (image: any): string | undefined =>
    typeof image === "string" ? image : image?.[imageKey];

  const getImageId = (image: any, index: number): string | number =>
    typeof image === "string" ? index : image?.[idKey] ?? index;

  if (!images || images.length === 0) {
    return (
      <Box mt={4}>
        <CustomTypography variant="heading5">{title}</CustomTypography>
        <CustomTypography
          variant="heading8"
          fontWeight={700}
          color={theme.palette.customColors.greyTextColor}
          style={{marginTop: "2rem"}}
        >
          {emptyMessage}
        </CustomTypography>
      </Box>
    );
  }

  return (
    <Box mt={4}>
      <CustomTypography variant="heading5">{title}</CustomTypography>
      <Box sx={{ minWidth: "15.5rem", display: "flex", gap: 1, mt: 2 }}>
        {images.slice(0, initialVisibleCount).map((image, index) => {
          const imageUrl = getImageUrl(image);
          return (
            <Box
              key={getImageId(image, index)}
              sx={{
                width: "5.5rem",
                height: "5.5rem",
                borderRadius: "8px",
                overflow: "hidden",
                position: "relative",
                cursor: "pointer",
                backgroundColor: theme.palette.grey[200],
                ...thumbnailStyle,
              }}
              onClick={() => imageUrl && onImageClick?.(image, index)}
            >
              <img
                src={imageUrl}
                alt={`Image ${index + 1}`}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = "";
                  target.alt = "Image error";
                }}
              />
            </Box>
          );
        })}

        {images.length > initialVisibleCount && (
          <Box
            sx={{
              width: "5.5rem",
              height: "5.5rem",
              borderRadius: "8px",
              overflow: "hidden",
              backgroundColor: "gray",
              position: "relative",
              cursor: "pointer",
              ...thumbnailStyle,
            }}
            onClick={() =>
              onImageClick?.(images[initialVisibleCount], initialVisibleCount)
            }
          >
            <img
              src={getImageUrl(images[initialVisibleCount])}
              alt="More images"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                filter: "brightness(0.3)",
              }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = "";
              }}
            />
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                color: "white",
                fontSize: "1rem",
                fontWeight: "bold",
              }}
            >
              +{images.length - initialVisibleCount}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default observer(ImageGalleryWithDialog);
